import React, {useEffect, useState} from 'react';
import axios from 'axios';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faUserNurse, faIdBadge, faMapMarkerAlt, faBriefcaseMedical,
    faExclamationTriangle, faCheckCircle, faClock, faUsers, faCalendarAlt, faCoins,
} from '@fortawesome/free-solid-svg-icons';
import {useNavigate} from 'react-router-dom';
import {useBaseUrl} from '../utils/utils';
import ProviderShell, {getStoredToken} from './layouts/ProviderShell';

/**
 * Individual Service Provider dashboard home (dashboard_architecture_plan.md,
 * C9). Profile overview + quick links into the rest of the C9 page set
 * (9.1-9.8), all real now rather than dead-end cards.
 */

type Profile = {
    full_name: string;
    email: string;
    phone: string | null;
    specialization: string;
    town: string | null;
    bio: string;
    years_experience: number;
    license_number: string;
    license_expiry: string | null;
    is_license_expiring_soon: boolean;
    verification_status: 'pending' | 'verified' | 'rejected' | 'suspended';
    created: string;
};

const VERIFICATION_META: Record<string, { label: string; color: string; icon: any }> = {
    pending: {label: 'Pending Review', color: '#f7b84b', icon: faClock},
    verified: {label: 'Verified', color: '#0ab39c', icon: faCheckCircle},
    rejected: {label: 'Rejected', color: '#f06548', icon: faExclamationTriangle},
    suspended: {label: 'Suspended', color: '#f06548', icon: faExclamationTriangle},
};

const ProviderHome: React.FC = () => {
    const baseUrl = useBaseUrl();
    const navigate = useNavigate();
    const [profile, setProfile] = useState<Profile | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        document.title = 'My Practice - Uzima Healthcare';
        const token = getStoredToken();
        if (!token) {
            navigate('/institution/newlog');
            return;
        }
        axios.get(`${baseUrl}practitioners/me/`, {
            headers: {Authorization: `Bearer ${token}`},
        }).then((res) => {
            setProfile(res.data?.data || null);
        }).catch(() => {
            setError('Could not load your profile. Please sign in again.');
        }).finally(() => setLoading(false));
    }, [baseUrl, navigate]);

    if (loading) {
        return (
            <ProviderShell>
                <div className="d-flex align-items-center justify-content-center" style={{minHeight: '60vh'}}>
                    <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                </div>
            </ProviderShell>
        );
    }

    if (error || !profile) {
        return (
            <ProviderShell>
                <div className="d-flex flex-column align-items-center justify-content-center gap-3" style={{minHeight: '60vh'}}>
                    <p className="text-danger">{error || 'Profile unavailable.'}</p>
                    <button className="btn btn-primary" onClick={() => navigate('/institution/newlog')}>Back to Sign In</button>
                </div>
            </ProviderShell>
        );
    }

    const verification = VERIFICATION_META[profile.verification_status] || VERIFICATION_META.pending;

    return (
        <ProviderShell>
            <div className="mb-4">
                <h2 className="fw-bold mb-1">
                    <FontAwesomeIcon icon={faUserNurse} className="me-2 text-primary"/>
                    Welcome, Dr. {profile.full_name}
                </h2>
                <p className="text-muted mb-0">{profile.specialization}</p>
            </div>

            {profile.verification_status !== 'verified' && (
                <div className="card mb-4" style={{borderLeft: `4px solid ${verification.color}`}}>
                    <div className="card-body d-flex align-items-center gap-3">
                        <FontAwesomeIcon icon={verification.icon} size="2x" style={{color: verification.color}}/>
                        <div>
                            <h6 className="mb-1">Account {verification.label}</h6>
                            <p className="text-muted small mb-0">
                                {profile.verification_status === 'pending'
                                    ? "Your credentials are being reviewed. Some features stay limited until verification completes."
                                    : 'Please contact support regarding your account status.'}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {profile.is_license_expiring_soon && (
                <div className="card mb-4" style={{borderLeft: '4px solid #f06548'}}>
                    <div className="card-body d-flex align-items-center gap-3">
                        <FontAwesomeIcon icon={faExclamationTriangle} size="2x" style={{color: '#f06548'}}/>
                        <div>
                            <h6 className="mb-1">License Expiring Soon</h6>
                            <p className="text-muted small mb-0">Your license expires on {profile.license_expiry} - renew to avoid a lapse in your active status.</p>
                        </div>
                    </div>
                </div>
            )}

            <div className="row">
                <div className="col-lg-8 mb-4">
                    <div className="card h-100">
                        <div className="card-header"><strong>Practice Profile</strong></div>
                        <div className="card-body">
                            <dl className="row mb-0">
                                <dt className="col-sm-4 text-muted">Specialization</dt>
                                <dd className="col-sm-8"><FontAwesomeIcon icon={faBriefcaseMedical} className="me-2 text-muted"/>{profile.specialization}</dd>

                                <dt className="col-sm-4 text-muted">Location</dt>
                                <dd className="col-sm-8"><FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 text-muted"/>{profile.town || 'Not set'}</dd>

                                <dt className="col-sm-4 text-muted">Experience</dt>
                                <dd className="col-sm-8">{profile.years_experience} year(s)</dd>

                                <dt className="col-sm-4 text-muted">License</dt>
                                <dd className="col-sm-8"><FontAwesomeIcon icon={faIdBadge} className="me-2 text-muted"/>{profile.license_number || 'Not provided'}</dd>

                                <dt className="col-sm-4 text-muted">Email</dt>
                                <dd className="col-sm-8">{profile.email}</dd>

                                <dt className="col-sm-4 text-muted">Phone</dt>
                                <dd className="col-sm-8">{profile.phone || 'Not provided'}</dd>

                                {profile.bio && (
                                    <>
                                        <dt className="col-sm-4 text-muted">Bio</dt>
                                        <dd className="col-sm-8">{profile.bio}</dd>
                                    </>
                                )}
                            </dl>
                        </div>
                    </div>
                </div>

                <div className="col-lg-4 mb-4">
                    <div className="card h-100">
                        <div className="card-header"><strong>Account Status</strong></div>
                        <div className="card-body">
                            <span className="badge mb-3" style={{backgroundColor: `${verification.color}1f`, color: verification.color}}>
                                <FontAwesomeIcon icon={verification.icon} className="me-1"/>
                                {verification.label}
                            </span>
                            {profile.verification_status !== 'verified' && (
                                <p className="small text-muted mb-0">
                                    Some features stay limited until your credentials finish verification.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-md-4 mb-4">
                    <div className="card h-100" style={{cursor: 'pointer'}} onClick={() => navigate('/provider/patients')}>
                        <div className="card-body d-flex align-items-center gap-3">
                            <FontAwesomeIcon icon={faUsers} size="2x" className="text-primary"/>
                            <div>
                                <h6 className="mb-1">My Patients</h6>
                                <p className="text-muted small mb-0">View and manage your patient roster</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4 mb-4">
                    <div className="card h-100" style={{cursor: 'pointer'}} onClick={() => navigate('/provider/appointments')}>
                        <div className="card-body d-flex align-items-center gap-3">
                            <FontAwesomeIcon icon={faCalendarAlt} size="2x" className="text-primary"/>
                            <div>
                                <h6 className="mb-1">Appointments</h6>
                                <p className="text-muted small mb-0">Manage your schedule and availability</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4 mb-4">
                    <div className="card h-100" style={{cursor: 'pointer'}} onClick={() => navigate('/provider/billing')}>
                        <div className="card-body d-flex align-items-center gap-3">
                            <FontAwesomeIcon icon={faCoins} size="2x" className="text-primary"/>
                            <div>
                                <h6 className="mb-1">Billing & Earnings</h6>
                                <p className="text-muted small mb-0">Track invoices and payments</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ProviderShell>
    );
};

export default ProviderHome;
